/* Inline business-proof upload, rendered inside the Company details panel.
   Before this, proof could only be attached through the Edit Business Profile
   modal (which still has its own FileUpload field for the same document). */
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

// Components
import Spinner from "../../../UI/Spinner/Spinner";

// Icons
import { I } from "../billing/icons";

// Constants
import * as businessConsts from "../../../../constants/business-profile/business-profile.constants";

// Form Models
import { BussinesForm } from "../../../../models/app/forms/Register-form.model";

// Actions
import { updateBusinessProfile } from "../../../../redux/actions/business.actions";

// Calls
import { fileUpload } from "../../../../api/calls/file-upload.call";

export const PROOF_MISSING = "missing";
export const PROOF_PENDING = "pending";
export const PROOF_VERIFIED = "verified";
export const PROOF_REJECTED = "rejected";

/* There is no "rejected" verification_status and no declined_at on the entity.
   Every upload path runs through attachProofDocumentToBusiness, which forces
   the business back to `pending` — so "not_verified WITH a document on file"
   can only mean a reviewer rejected it. */
export const proofStatus = business => {
  if (business?.verificationStatus === businessConsts.BUSINESS_STATUS_PENDING) {
    return PROOF_PENDING;
  }
  if (business?.verificationStatus === businessConsts.BUSINESS_STATUS_VERIFIED) {
    return PROOF_VERIFIED;
  }
  return business?.proofDocument?.url ? PROOF_REJECTED : PROOF_MISSING;
};

const BADGES = {
  [PROOF_MISSING]: { label: "Not verified", className: "badge-neutral" },
  [PROOF_PENDING]: { label: "Under review", className: "badge-warn", Icon: I.Clock },
  [PROOF_VERIFIED]: { label: "Verified", className: "badge-success", Icon: I.Check },
  [PROOF_REJECTED]: { label: "Action needed", className: "badge-error", Icon: I.Alert }
};

export const ProofBadge = ({ status }) => {
  const { label, className, Icon } = BADGES[status] || BADGES[PROOF_MISSING];
  return (
    <span className={`badge ${className}`}>
      {Icon && <Icon width="12" height="12" />}
      {label}
    </span>
  );
};

const ACCEPTED_TYPES = ["application/pdf", "image/png", "image/jpeg"];
const MAX_BYTES = 10 * 1024 * 1024;

// FileDTO carries only { url, type, mime_type } — no original filename.
const fileNameFromUrl = url => {
  const last = String(url).split("?")[0].split("/").pop();
  if (!last) return "Business proof";
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
};

const BusinessProofBlock = ({ business, location, branchName, ping }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  const status = proofStatus(business);
  const needsProof = status === PROOF_MISSING || status === PROOF_REJECTED;
  const isPinging = ping && needsProof;
  // A business can be verified straight from the system portal without any
  // document ever being attached — then there is no file to put in a chip.
  const proofUrl = business?.proofDocument?.url;
  // Private object: only the presigned url opens. It is minted per response and
  // expires in minutes, so there is nothing to cache here.
  const proofViewUrl = business?.proofDocumentViewUrl;

  const openFilePicker = () => inputRef.current && inputRef.current.click();

  const handleFile = async file => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error("Business proof must be a PDF, JPG or PNG file.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Business proof must be 10 MB or smaller.");
      return;
    }

    setIsLoading(true);
    const response = await fileUpload(file);
    if (response.hasError) {
      toast.error(response.error.message);
      setIsLoading(false);
      return;
    }

    // Send the whole form and let removeSameValues() strip what didn't change.
    // A `{ proofDocumentationUrl }`-only payload still serialises an empty
    // `location`, and the BE rebuilds the location from whatever it receives —
    // which would wipe the real one.
    const initialValues = new BussinesForm({ ...business, ...location });
    dispatch(
      updateBusinessProfile(
        { ...initialValues, proofDocumentationUrl: response.url },
        initialValues
      )
    );
    // The saga toasts on failure and refreshes `business` on success, which is
    // what flips this block to the pending chip.
    setIsLoading(false);
  };

  const onDrop = event => {
    event.preventDefault();
    setIsDragging(false);
    handleFile(event.dataTransfer.files[0]);
  };

  return (
    <>
      <div className="bp-proof-head">
        <div className="bp-proof-title">
          Business proof
          {isPinging && (
            <span className="bp-proof-need">
              <I.Alert width="11" height="11" /> Action needed
            </span>
          )}
        </div>
        <div className="bp-proof-sub">
          A business license, registration or tax document showing your legal
          business name. Required before {branchName || "your branch"} can go
          live and take jobs.
        </div>
      </div>

      {needsProof ? (
        <>
          {status === PROOF_REJECTED && (
            <div className="inline-err" style={{ marginBottom: 12 }}>
              <I.Alert width="14" height="14" />
              <span>
                <b>Previous document rejected.</b> Please upload a clearer copy
                showing your legal business name.
              </span>
            </div>
          )}
          <div
            className={`bp-proof-drop${isDragging ? " drag" : ""}${
              isPinging ? " bp-proof-ping" : ""
            }${isLoading ? " is-loading" : ""}`}
            onClick={isLoading ? undefined : openFilePicker}
            onDragOver={event => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={isLoading ? undefined : onDrop}
          >
            <div className="bp-proof-drop-ico">
              {isLoading ? <Spinner size={22} /> : <I.Upload width="22" height="22" />}
            </div>
            <div className="bp-proof-drop-title">
              {isLoading
                ? "Uploading…"
                : status === PROOF_REJECTED
                ? "Upload a new document"
                : "Upload your business proof"}
            </div>
            <div className="bp-proof-drop-text">
              Drag &amp; drop your file here, or <span className="link">browse</span>{" "}
              to choose one.
            </div>
            <div className="bp-proof-drop-hint">
              <I.Lock width="12" height="12" /> PDF, JPG or PNG · up to 10 MB ·
              stored securely
            </div>
          </div>
        </>
      ) : !proofUrl ? (
        <div className="bp-note">
          <I.Info width="14" height="14" />
          {status === PROOF_VERIFIED
            ? "Verified by TruckFix — no document on file."
            : "Verification in progress — no document on file."}
        </div>
      ) : (
        <>
          <div className={`bp-proof-file is-${status}`}>
            <div className="bp-proof-fico">
              {isLoading ? <Spinner size={18} /> : <I.Doc width="20" height="20" />}
            </div>
            <div className="bp-proof-fmeta">
              <div className="bp-proof-fname">{fileNameFromUrl(proofUrl)}</div>
              <a
                className="link bp-proof-flink"
                href={proofViewUrl}
                target="_blank"
                rel="noreferrer"
              >
                <I.External width="12" height="12" /> View document
              </a>
            </div>
            <div className="bp-proof-fright">
              <ProofBadge status={status} />
              {/* The BE refuses proof updates once verified, so offering
                  "Replace" there would be a silent no-op. */}
              {status === PROOF_PENDING && (
                <button
                  type="button"
                  className="link"
                  disabled={isLoading}
                  onClick={openFilePicker}
                >
                  Replace
                </button>
              )}
            </div>
          </div>
          {status === PROOF_PENDING && (
            <div className="bp-note">
              <I.Clock width="14" height="14" /> Verification is usually done
              within one business day. We&apos;ll notify you.
            </div>
          )}
        </>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,image/png,image/jpeg"
        style={{ display: "none" }}
        onChange={event => {
          handleFile(event.target.files[0]);
          event.target.value = "";
        }}
      />
    </>
  );
};

export default BusinessProofBlock;
